import { grey } from '@mui/material/colors'
import { ThemeOptions } from '@mui/material/styles'

export const CARD_HEADER_MIN_HEIGHT = 49

export const commonOptions = {
  palette: {
    primary: {
      main: '#FF4D00',
      contrastText: '#FFFFFF'
    },
    error: {
      main: '#FF0000'
    },
    success: {
      main: '#70C072'
    },
    warning: {
      main: '#ff9800'
    },
    secondary: { light: grey[700], main: grey[800], dark: grey[900], contrastText: grey[50] },
    consoleGrey: {
      0: '#FFFFFF',
      1000: '#26292E',
      1100: '#222429',
      1200: '#55555566',
      1300: '#5B636E'
    },
    IMGArena: {
      50: '#FFEDE5',
      100: '#FFDBCC',
      200: '#FFB899',
      300: '#FF9466',
      400: '#FF7133',
      500: '#FF4D00',
      600: '#CC3E00',
      700: '#992E00',
      800: '#992E00',
      900: '#330F00'
    },

    scrollbar: {
      thumb: {
        vertical: {
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '2px',
          width: '4px',
          margin: '0 0 0 2px',
          zIndex: 100
        },
        horizontal: {
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '3px',
          height: '5px',
          margin: '3px 0 0 0',
          zIndex: 100
        }
      }
    },
    golfColours: {
      eagle: '#e3717a',
      birdie: '#F5BBB7',
      bogey: '#b4d4ef',
      doubleBogey: '#67add9'
    }
  },

  shape: {
    borderRadius: 4
  },

  typography: {
    fontFamily: '"Inter", Arial',
    fontSize: 12,
    caption: {
      fontWeight: 600
    },
    overline: {
      fontWeight: 600,
      letterSpacing: '0.0833em'
    },
    subtitle2: {
      lineHeight: 1.4
    }
  },

  components: {
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          position: 'sticky',
          backgroundColor: theme.palette.secondary.dark,
          color: grey[100],
          backgroundImage: 'unset'
        })
      }
    },

    MuiRadio: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.size === 'small' && {
            '.MuiSvgIcon-root': { fontSize: 16 },
            '&, & + .MuiFormControlLabel-label': {
              fontSize: 14
            }
          }),
          ...(ownerState.size === 'medium' && {
            '.MuiSvgIcon-root': { fontSize: 24 },
            '&, & + .MuiFormControlLabel-label': {
              fontSize: 16
            }
          })
        })
      }
    },

    MuiCheckbox: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.size === 'small' && {
            '.MuiSvgIcon-root': { fontSize: 16 },
            '&, & + .MuiFormControlLabel-label': {
              fontSize: 14
            }
          }),
          ...(ownerState.size === 'medium' && {
            '.MuiSvgIcon-root': { fontSize: 22 },
            '&, & + .MuiFormControlLabel-label': {
              fontSize: 16
            }
          })
        })
      }
    },

    MuiSwitch: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          ...(ownerState.size === 'small' && {
            '&, & + .MuiFormControlLabel-label': {
              fontSize: 14
            }
          }),
          ...(ownerState.size === 'medium' && {
            '&, & + .MuiFormControlLabel-label': {
              fontSize: 14
            }
          }),
          '& .MuiSwitch-switchBase': {
            '& .MuiSwitch-thumb': {
              color: theme.palette.common.white
            },
            '&.Mui-checked': {
              '& + .MuiSwitch-track': {
                backgroundColor: theme.palette.primary.main,
                opacity: 1
              }
            }
          }
        })
      }
    },

    MuiToolbar: {
      styleOverrides: {
        root: ({ theme }) => ({
          height: 64,
          padding: `0 ${theme.spacing(1)} !important`
        })
      }
    },

    MuiFormControl: {
      defaultProps: {
        margin: 'dense',
        size: 'small'
      },
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiOutlinedInput-root ': {
            borderRadius: theme.shape.borderRadius
          },
          '& .MuiFormLabel-root': {
            fontSize: 14
          }
        })
      }
    },

    MuiTextField: {
      defaultProps: {
        margin: 'dense',
        size: 'small',
        variant: 'outlined'
      },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius
        })
      }
    },

    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiOutlinedInput-notchedOutline': {
            fontSize: 14
          },
          backgroundColor: theme.palette.background.paper
        })
      }
    },

    MuiDialog: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiDialog-paper': {
            borderRadius: theme.shape.borderRadius
          }
        })
      }
    },

    MuiMenu: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiPaper-root': {
            borderRadius: theme.shape.borderRadius,
            marginTop: 3
          }
        })
      }
    },

    MuiChip: {
      styleOverrides: {
        root: {
          '&.MuiChip-sizeSmall': {
            height: 24,
            fontSize: '.75rem',
            padding: 1
          }
        }
      }
    },

    MuiAutocomplete: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius
        }),
        inputRoot: {
          '& .MuiInputBase-inputSizeSmall': {
            fontSize: 14
          }
        }
      }
    },

    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          top: 0,
          marginBottom: 0,
          paddingRight: theme.spacing(1.25)
        })
      }
    },

    MuiButton: {
      defaultProps: {
        variant: 'contained',
        size: 'medium',
        color: 'primary'
      },
      variants: [
        {
          props: { variant: 'contained', color: 'primary' },
          style: () => ({
            '&:hover, &:focus, &:active': {
              boxShadow: 'none'
            }
          })
        }
      ],
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          ...(ownerState.size === 'small' && {
            height: 32,
            fontSize: 12,
            padding: 8,
            lineHeight: 1.333
          }),
          ...(ownerState.size === 'medium' && {
            height: 36,
            fontSize: 13,
            padding: theme.spacing(2),
            lineHeight: 1.75
          }),
          ...(ownerState.size === 'large' && { height: 48, fontSize: 16, padding: 16 }),
          minWidth: 'max-content',
          boxShadow: 'none'
        })
      }
    },

    MuiFormLabel: {
      variants: [
        {
          props: { error: true },
          style: ({ theme }) => ({
            color: theme.palette.error.main,
            fontSize: 12,
            paddingLeft: 1,
            paddingBottom: 2
          })
        }
      ]
    },

    MuiSelect: {
      defaultProps: {
        size: 'small'
      }
    },

    MuiPopper: {
      defaultProps: {
        sx: {
          '& .MuiPaper-root': (theme) => ({
            borderRadius: Number(theme.shape.borderRadius) / 2
          })
        }
      }
    }
  }
} satisfies ThemeOptions
